import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Loader2, UserPlus, KeyRound, Mail, Trash2, Plus } from 'lucide-react';
import api from '../lib/api';
import { when } from './PlatformConsole';

const detail = (e) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail
  : Array.isArray(e?.response?.data?.detail) ? e.response.data.detail.map((d) => d.msg).join(' ') : 'Something went wrong');
const EMPTY = { name: '', email: '', role_id: '', temp_password: '' };

const EnrolForm = ({ roles, onDone }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post('/platform/team/members', form);
      toast.success(data.invite_sent ? `${form.name} enrolled — invite email sent to ${form.email}`
        : `${form.name} enrolled, but the invite email did not send${data.invite_error ? ` (${data.invite_error})` : ''}. Share the temporary password privately.`);
      setForm(EMPTY); onDone();
    }
    catch (err) { toast.error(detail(err)); } finally { setBusy(false); }
  };
  return (
    <form className="pc-card pc-team-form" onSubmit={submit} data-testid="pc-team-enrol-form">
      <div className="pc-card-h"><h2>Enrol staff member</h2></div>
      <label className="pc-field">Full name<input className="pc-input" required minLength={2} value={form.name} onChange={set('name')} data-testid="pc-team-name" /></label>
      <label className="pc-field">Work email<input className="pc-input" type="email" required value={form.email} onChange={set('email')} data-testid="pc-team-email" /></label>
      <label className="pc-field">Role
        <select className="pc-select" required value={form.role_id} onChange={set('role_id')} data-testid="pc-team-role">
          <option value="">Choose role</option>
          {roles.map((r) => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
        </select>
      </label>
      <label className="pc-field">Temporary password (12+ characters)<input className="pc-input" type="text" required minLength={12} value={form.temp_password} onChange={set('temp_password')} autoComplete="new-password" data-testid="pc-team-temp-password" /></label>
      <p className="pc-muted">We email them a secure link to set their own password. The temporary password above is a fallback if the email does not arrive. They can also sign in with Google using this email.</p>
      <button className="pc-btn pc-btn-primary" disabled={busy} data-testid="pc-team-enrol-submit">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Enrol</button>
    </form>
  );
};

const ResetDialog = ({ m, onClose, onSave }) => {
  const [pw, setPw] = useState('');
  return (
    <div className="pc-modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="pc-modal" data-testid="pc-team-reset-dialog" onSubmit={(e) => { e.preventDefault(); onSave(pw); }}>
        <h3>Reset password</h3>
        <p className="pc-muted">Set a new temporary password for {m.email}. Their open sessions end and they must choose a new password at next sign-in.</p>
        <label className="pc-field">Temporary password (12+ characters)<input className="pc-input" autoFocus required minLength={12} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="pc-team-reset-password" /></label>
        <div className="pc-actions"><button type="button" className="pc-btn" onClick={onClose} data-testid="pc-team-reset-cancel">Cancel</button><button className="pc-btn pc-btn-primary" disabled={pw.length < 12} data-testid="pc-team-reset-submit">Reset password</button></div>
      </form>
    </div>
  );
};

const MemberRow = ({ m, roles, onDone }) => {
  const [resetting, setResetting] = useState(false);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); onDone(); return true; } catch (e) { toast.error(detail(e)); return false; } };
  const reset = async (pw) => { if (await act(() => api.post(`/platform/team/members/${m.user_id}/reset-password`, { temp_password: pw }), 'Password reset — they must change it on next sign-in')) setResetting(false); };
  const toggle = () => {
    if (m.is_active && !window.confirm(`Remove console access for ${m.name}? They are signed out immediately.`)) return;
    act(() => api.patch(`/platform/team/members/${m.user_id}`, { is_active: !m.is_active }), m.is_active ? 'Access removed' : 'Access restored');
  };
  return (
    <tr data-testid={`pc-team-member-${m.user_id}`}>
      <td><b>{m.name}</b><div className="pc-muted">{m.email}</div></td>
      <td>
        <select className="pc-select" value={m.platform_role} onChange={(e) => act(() => api.patch(`/platform/team/members/${m.user_id}`, { role_id: e.target.value }), 'Role updated')} data-testid={`pc-team-member-role-${m.user_id}`}>
          {roles.map((r) => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
        </select>
      </td>
      <td><span className={`pc-pill ${m.is_active ? 'pc-pill-active' : 'pc-pill-suspended'}`} data-testid={`pc-team-member-status-${m.user_id}`}>{m.is_active ? 'active' : 'deactivated'}</span>{m.invite_sent_at ? <div className="pc-muted" data-testid={`pc-team-member-invite-${m.user_id}`}>invite pending</div> : m.must_change_password && <div className="pc-muted">password change pending</div>}</td>
      <td>{when(m.created_at)}</td>
      <td data-testid={`pc-team-member-last-login-${m.user_id}`}>{m.last_login_at ? when(m.last_login_at) : 'Never'}</td>
      <td className="pc-num pc-team-actions">
        <div className="pc-row-actions">
        {m.is_active && <button type="button" className="pc-btn" onClick={() => act(() => api.post(`/platform/team/members/${m.user_id}/invite`), `Invite email sent to ${m.email}`)} aria-label={`Send invite email to ${m.name}`} title="Send invite email" data-testid={`pc-team-member-resend-${m.user_id}`}><Mail className="h-3.5 w-3.5" /></button>}
        <button type="button" className="pc-btn" onClick={() => setResetting(true)} aria-label={`Reset password for ${m.name}`} title="Reset password" data-testid={`pc-team-member-reset-${m.user_id}`}><KeyRound className="h-3.5 w-3.5" /></button>
        {resetting && createPortal(<ResetDialog m={m} onClose={() => setResetting(false)} onSave={reset} />, document.body)}
        <button type="button" className={`pc-btn ${m.is_active ? 'pc-btn-danger' : ''}`} onClick={toggle} data-testid={`pc-team-member-toggle-${m.user_id}`}>{m.is_active ? 'Deactivate' : 'Reactivate'}</button>
        </div>
      </td>
    </tr>
  );
};

const NewRoleForm = ({ onDone }) => {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post('/platform/team/roles', { name, permissions: { overview: 'view' } }); toast.success(`${name} created — set its access in the grid`); setName(''); onDone(); }
    catch (err) { toast.error(detail(err)); } finally { setBusy(false); }
  };
  return (
    <form className="pc-team-newrole" onSubmit={submit} data-testid="pc-role-create-form">
      <input className="pc-input" placeholder="New role name, e.g. Onboarding" required minLength={2} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} data-testid="pc-role-name" />
      <button className="pc-btn pc-btn-primary" disabled={busy} data-testid="pc-role-create-submit">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create role</button>
    </form>
  );
};

const Matrix = ({ data, onDone }) => {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState('');
  const value = (r, k) => draft[r.role_id]?.[k] ?? r.permissions[k] ?? 'none';
  const set = (r, k, v) => setDraft((d) => ({ ...d, [r.role_id]: { ...(d[r.role_id] || {}), [k]: v } }));
  const save = async (r) => {
    setSaving(r.role_id);
    try { await api.put(`/platform/team/roles/${r.role_id}`, { permissions: { ...r.permissions, ...draft[r.role_id] } }); toast.success(`${r.name} permissions saved`); setDraft((d) => ({ ...d, [r.role_id]: undefined })); onDone(); }
    catch (e) { toast.error(detail(e)); } finally { setSaving(''); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete the ${r.name} role?`)) return;
    try { await api.delete(`/platform/team/roles/${r.role_id}`); toast.success(`${r.name} deleted`); onDone(); }
    catch (e) { toast.error(detail(e)); }
  };
  return (
    <div className="pc-card" data-testid="pc-team-matrix">
      <div className="pc-card-h"><h2>Permissions matrix</h2><span className="pc-muted">None · View (read only) · Manage (make changes)</span></div>
      <NewRoleForm onDone={onDone} />
      <div className="pc-scroll">
        <table className="pc-table pc-matrix">
          <thead><tr><th>Section</th>{data.roles.map((r) => <th key={r.role_id}>{r.name}<div className="pc-muted">{r.members} staff{r.custom ? ' · custom' : ''}</div></th>)}</tr></thead>
          <tbody>
            {data.sections.map((s) => (
              <tr key={s.key}>
                <td>{s.label}</td>
                {data.roles.map((r) => (
                  <td key={r.role_id}>
                    <select className={`pc-select pc-level-${value(r, s.key)}`} value={value(r, s.key)} onChange={(e) => set(r, s.key, e.target.value)} data-testid={`pc-matrix-${r.role_id}-${s.key}`}>
                      {data.levels.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </td>
                ))}
              </tr>
            ))}
            <tr><td />{data.roles.map((r) => (
              <td key={r.role_id}>
                <div className="pc-matrix-actions">
                <button type="button" className="pc-btn pc-btn-primary" disabled={!draft[r.role_id] || saving === r.role_id} onClick={() => save(r)} data-testid={`pc-matrix-save-${r.role_id}`}>Save</button>
                {r.custom && <button type="button" className="pc-btn pc-btn-danger" onClick={() => remove(r)} aria-label={`Delete ${r.name}`} title="Delete role" data-testid={`pc-matrix-delete-${r.role_id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              </td>
            ))}</tr>
          </tbody>
        </table>
      </div>
      <p className="pc-muted">Signing in as a pharmacy stays with the platform owner only. Staff with Manage on Team &amp; roles can enrol colleagues and edit roles, but never their own access.</p>
    </div>
  );
};

export const PlatformTeam = () => {
  const [members, setMembers] = useState(null);
  const [roles, setRoles] = useState(null);
  const load = useCallback(() => Promise.all([api.get('/platform/team/members'), api.get('/platform/team/roles')])
    .then(([m, r]) => { setMembers(m.data.members); setRoles(r.data); }).catch((e) => toast.error(detail(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!members || !roles) return <div className="pc-muted" data-testid="pc-team-loading"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  return (
    <div data-testid="pc-team">
      <h1 className="pc-h1">Team & roles</h1>
      <p className="pc-sub">Enrol DutiLyne staff and decide what each role can see or change.</p>
      <div className="pc-team-top">
        <EnrolForm roles={roles.roles} onDone={load} />
        <div className="pc-card" data-testid="pc-team-members">
          <div className="pc-card-h"><h2>Platform staff</h2><span className="pc-muted">{members.length} enrolled</span></div>
          {!members.length ? <p className="pc-empty" data-testid="pc-team-empty">No staff enrolled yet.</p> : (
            <div className="pc-scroll"><table className="pc-table"><thead><tr><th>Name</th><th>Role</th><th>Status</th><th>Enrolled</th><th>Last sign-in</th><th /></tr></thead>
              <tbody>{members.map((m) => <MemberRow key={m.user_id} m={m} roles={roles.roles} onDone={load} />)}</tbody></table></div>
          )}
        </div>
      </div>
      <Matrix data={roles} onDone={load} />
    </div>
  );
};
